import { App, Spin } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/authService';
import { FileService } from '../../services/fileApiService';
import { useAppStore } from '../../store/store';
import { getApiErrorDetail } from '../../utils/apiErrorCode';
import { normalizeLoginReturnPath } from '../../utils/loginReturnUrl';

const fileService = new FileService();

/**
 * OAuth callback component that handles the OAuth redirect from Microsoft/Google
 * and exchanges the authorization code for Filegrant tokens.
 */
export const OAuthCallback = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { notification } = App.useApp();
  const { getUserInfo, redirectLoggedInUser } = useAuth();
  const setIsSsoLogin = useAppStore((s) => s.appEvents.ssoLogin.setIsSsoLogin);

  const [error, setError] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const isProcessing = useRef(false);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Prevent multiple executions (React Strict Mode, re-renders)
      if (isProcessing.current) {
        return;
      }
      isProcessing.current = true;

      try {
        // Get URL parameters
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check for OAuth error
        if (errorParam) {
          let errorMessage = t('errors.loginFailed');
          if (errorDescription) {
            errorMessage = errorDescription;
          } else if (errorParam === 'access_denied') {
            errorMessage = t('errors.oauthAccessDenied') || 'Access denied by user';
          }
          setError(errorMessage);
          notification.error({
            message: errorMessage,
          });
          // Redirect to login after a delay
          setTimeout(() => {
            navigate('/login');
          }, 2000);
          return;
        }

        // Validate required parameters
        if (!code || !state) {
          setError(t('errors.oauthInvalidCallback') || 'Invalid OAuth callback');
          notification.error({
            message: t('errors.oauthInvalidCallback') || 'Invalid OAuth callback',
          });
          setTimeout(() => {
            navigate('/login');
          }, 2000);
          return;
        }

        // Verify CSRF state
        const savedState = sessionStorage.getItem('oauth_state');

        // If savedState is null but we have code/state params, the callback might already
        // be processing (React Strict Mode double-execution). Mark as completed and wait.
        if (!savedState) {
          setIsCompleted(true);
          return;
        }

        if (savedState !== state) {
          setError(t('errors.oauthStateMismatch') || 'Security verification failed');
          notification.error({
            message: t('errors.oauthStateMismatch') || 'Security verification failed',
          });
          // Clean up to prevent loops
          sessionStorage.removeItem('oauth_state');
          sessionStorage.removeItem('oauth_tenant');
          sessionStorage.removeItem('oauth_provider');
          setTimeout(() => {
            navigate('/login');
          }, 2000);
          return;
        }

        // Get saved OAuth context
        const tenantUrl = sessionStorage.getItem('oauth_tenant');
        const provider = sessionStorage.getItem('oauth_provider') as 'microsoft' | 'google' | null;
        const clientId = sessionStorage.getItem('oauth_client_id');

        if (!tenantUrl || !provider || !clientId) {
          setError(t('errors.oauthContextMissing') || 'OAuth context missing');
          notification.error({
            message: t('errors.oauthContextMissing') || 'OAuth context missing',
          });
          setTimeout(() => {
            navigate('/login');
          }, 2000);
          return;
        }

        // Clean up sessionStorage (keep state for the API call)
        sessionStorage.removeItem('oauth_state');
        sessionStorage.removeItem('oauth_tenant');
        sessionStorage.removeItem('oauth_provider');
        sessionStorage.removeItem('oauth_client_id');

        // Retrieve the original URL saved before OAuth redirect
        const savedRedirectUrl = sessionStorage.getItem('oauth_redirect_url');
        sessionStorage.removeItem('oauth_redirect_url');

        // Exchange code for tokens (include state for backend validation)
        const redirectUri = `${window.location.origin}/oauth/callback`;
        await authService.authenticateWithOAuthCallback(
          provider,
          code,
          redirectUri,
          clientId,
          state,
        );
        setIsSsoLogin(true);

        // Small delay to ensure tokens are propagated to store and context
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Check if this login was initiated from the mobile app
        const isMobileApp = sessionStorage.getItem('mobileApp') === '1';

        // Extract fileId from saved redirect URL (e.g. /file/{id}?user=...)
        const savedFileId = savedRedirectUrl?.match(/^\/file\/([^/?]+)/)?.[1];
        const safeReturnUrl = normalizeLoginReturnPath(savedRedirectUrl);

        // Skip home redirect when we still need to land on a file or return URL
        const shouldRedirectHome = !isMobileApp && !savedFileId && !safeReturnUrl;
        await getUserInfo(shouldRedirectHome);

        // Small delay to ensure user info is loaded in store
        await new Promise((resolve) => setTimeout(resolve, 100));

        // If mobile app flow, redirect to mobile auth success page
        if (isMobileApp) {
          navigate('/mobile/auth-success');
          return;
        }

        // Handle file redirect if the login was initiated from a file link
        if (savedFileId) {
          try {
            const file = await fileService.getFile(savedFileId);
            if (file) {
              // Get user from store after getUserInfo has updated it
              const userFromStore = useAppStore.getState().authenticatedUser;
              if (file.isFolder) {
                if (userFromStore?.id && userFromStore.id.toString() === file.ownerId?.toString()) {
                  navigate(`/files/${FilesTableEnum.Personal}/folder/${file.uid}`);
                } else {
                  navigate(`/files/${FilesTableEnum.Received}/folder/${file.uid}`);
                }
              } else {
                navigate(`/viewer/${file.uid}`);
              }
              return;
            }
          } catch (fileError) {
            notification.error({
              title: t('errors.requestFilePermissions'),
            });
          }
        }

        // Honor shared return URL (e.g. /extension/authorize?extensionId=...)
        if (safeReturnUrl) {
          navigate(safeReturnUrl, { replace: true });
          return;
        }

        // Redirect to home
        redirectLoggedInUser();
      } catch (error: any) {
        console.error('OAuth callback error:', error);
        let errorMessage = t('errors.loginFailed') || 'Login failed';

        // Handle specific OAuth errors by name
        if (error?.response?.name) {
          switch (error.response.name) {
            case 'ProviderNotEnabled':
              errorMessage = t('errors.oauthProviderNotEnabled') || 'OAuth provider not enabled';
              break;
            case 'DirectOAuthNotEnabled':
              errorMessage =
                t('errors.oauthNotEnabled') || 'Direct OAuth not enabled for this tenant';
              break;
            case 'DomainNotAllowed':
              errorMessage = t('errors.oauthDomainNotAllowed') || 'Email domain not allowed';
              break;
            case 'EmailMismatch':
              errorMessage =
                t('errors.oauthEmailMismatch') ||
                'You signed in with a different account than the email you entered. Please sign in with the correct account.';
              break;
            case 'UserDisabled':
              errorMessage = t('errors.authenticationUserBlocked') || 'User account is disabled';
              break;
            case 'CodeExchangeFailed':
              errorMessage =
                t('errors.oauthCodeExchangeFailed') || 'Failed to exchange authorization code';
              break;
            case 'TokenValidationFailed':
              errorMessage = t('errors.oauthTokenValidationFailed') || 'Token validation failed';
              break;
            case 'InvalidTenant':
              errorMessage = t('errors.oauthInvalidTenant') || 'Invalid tenant';
              break;
            default:
              errorMessage = error.response.value || error.response.name || errorMessage;
          }
        } else if (error?.status === 429) {
          // Handle 429 Rate Limit
          errorMessage = t('errors.tooManyRequests');
        } else if (error?.status === 401) {
          // Handle 401 Unauthorized
          errorMessage = t('errors.unauthorized') || 'Authentication failed. Please try again.';
        } else if (error?.status === 403) {
          // 403 problem body: prefer `detail` (e.g. email/alias mismatch after IdP sign-in)
          errorMessage =
            getApiErrorDetail(error.response) ??
            getApiErrorDetail(error) ??
            t('errors.forbidden') ??
            'Access denied';
        } else if (error?.status === 500) {
          // Handle 500 Internal Server Error
          errorMessage = t('errors.serverError') || 'Server error. Please try again later.';
        } else if (error?.message) {
          // Handle "Invalid or expired session" error
          if (error.message.includes('Invalid or expired')) {
            errorMessage = t('errors.oauthSessionExpired') || 'Session expired. Please try again.';
          } else {
            errorMessage = error.message;
          }
        }

        setError(errorMessage);
        notification.error({
          message: t('errors.loginFailed') || 'Login failed',
          description: errorMessage,
        });
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate, notification, t, getUserInfo, redirectLoggedInUser, setIsSsoLogin]);

  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center">
          <p className="text-red-dark mb-4 text-lg">{error}</p>
          <p className="text-gray-dark text-sm">
            {t('common.redirectingToLogin') || 'Redirecting to login...'}
          </p>
        </div>
      </div>
    );
  }

  // Show spinner while processing or when already completed (waiting for redirect)
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <div className="text-center">
        <Spin size="large" />
        <p className="text-gray-dark mt-4">
          {isCompleted
            ? t('common.redirecting') || 'Redirecting...'
            : t('common.completingLogin') || 'Completing login...'}
        </p>
      </div>
    </div>
  );
};

export default OAuthCallback;
